import { defineAsyncComponent, type Component } from 'vue';

import BigImage from '@/features/articleContent/BigImage.vue';
import BlockText from '@/features/articleContent/BlockText.vue';
import Emphasis from '@/features/articleContent/Emphasis.vue';
import GuidesQuotes from '@/features/articleContent/guidesQuotes/GuidesQuotes.vue';
import Heading from '@/features/articleContent/Heading.vue';
import HTMLBlock from '@/features/articleContent/HTMLBlock.vue';
import Images from '@/features/articleContent/Images.vue';
import Lanebreaker from '@/features/articleContent/Lanebreaker.vue';
import Link from '@/features/articleContent/Link.vue';
import List from '@/features/articleContent/List.vue';
import ListItem from '@/features/articleContent/ListItem.vue';
import type { ContentItem, CustomComponent } from '@/features/articleContent/models/ArticleContent';
import Paragraph from '@/features/articleContent/Paragraph.vue';
import RelatedGuides from '@/features/articleContent/relatedGuides/RelatedGuides.vue';
import Strong from '@/features/articleContent/Strong.vue';
import Text from '@/features/articleContent/Text.vue';
import PartnersExpsWidget from '@/features/articleContent/widgets/PostPartnerWidget.vue';
import { MobileAppPromo } from '@/shared/ui/articleBanners';

import LinkCard from '../LinkCard.vue';

const InfoBlock = defineAsyncComponent(() => import('@/features/articleContent/InfoBlock.vue'));

export interface PreloadedData {
  linkedArticles: Array<{
    id: number;
    type: 'article' | 'sight';
    slug: string;
    title: string;
    subtitle: string;
    img: string;
    readingTime?: number;
  }>;
  /**
   * Контекст поста журнала для {@link Images} / GalleryTrigger (заголовок, ссылка в ориджине).
   */
  articlePost?: {
    id: number;
    title: string;
    url?: string;
  };
}

const getCustomComponentNameAndProps = (component: CustomComponent) => {
  switch (component.attrs.component) {
    case 'experiences': {
      const { attrs } = component;

      return {
        component: PartnersExpsWidget,
        props: { ...attrs },
      };
    }
    case 'link-card': {
      return {
        component: LinkCard,
        props: { value: component.data },
      };
    }

    case 'banner-placeholder':
    case 'banner_placeholder': {
      const { kind } = component.attrs;

      return {
        component: MobileAppPromo,
        props: { kind },
      };
    }

    default:
      return null;
  }
};

export type GetComponentNameAndPropsResult = {
  component: Component;
  props: Record<string, unknown>;
} | null;

export const getComponentNameAndProps = (
  item: ContentItem,
  preloaded?: PreloadedData,
): GetComponentNameAndPropsResult => {
  switch (item.type) {
    case 'heading':
      return {
        component: Heading,
        props: {
          id: item.attrs.id,
          level: item.attrs.level,
          children: item.children,
          preloadedData: preloaded,
        },
      };
    case 'text':
      return {
        component: Text,
        props: {
          value: item.raw,
        },
      };
    case 'paragraph':
      return {
        component: Paragraph,
        props: {
          children: item.children,
          preloadedData: preloaded,
        },
      };
    case 'list':
      return {
        component: List,
        props: {
          children: item.children,
          preloadedData: preloaded,
          ordered: item.attrs?.ordered ?? false,
          tight: item.tight ?? false,
        },
      };
    case 'list_item':
      return {
        component: ListItem,
        props: {
          children: item.children,
          preloadedData: preloaded,
        },
      };
    case 'block_text':
      return {
        component: BlockText,
        props: {
          children: item.children,
          preloadedData: preloaded,
        },
      };
    case 'linebreak':
      return {
        component: Lanebreaker,
        props: {},
      };
    case 'strong':
      return {
        component: Strong,
        props: {
          children: item.children,
          preloadedData: preloaded,
        },
      };
    case 'emphasis':
      return {
        component: Emphasis,
        props: {
          children: item.children,
          preloadedData: preloaded,
        },
      };
    case 'images':
      return {
        component: Images,
        props: {
          caption: item.attrs.caption,
          images: item.attrs.images.map((image) => ({
            src: image,
            alt: '',
          })),
          preloadedData: preloaded,
        },
      };
    case 'cap_image':
      return {
        component: BigImage,
        props: {
          src: item.attrs.url,
          alt: item.attrs.alt,
          caption: item.attrs.caption,
          preloadedData: preloaded,
        },
      };
    case 'link':
      return {
        component: Link,
        props: {
          url: item.attrs.url,
          linkType: item.attrs.type,
          children: item.children,
          preloadedData: preloaded,
        },
      };
    case 'block_html':
      return {
        component: HTMLBlock,
        props: {
          value: item.raw,
        },
      };

    case 'guides_quotes':
      return {
        component: GuidesQuotes,
        props: { quotes: item.attrs?.quotes, guidesData: item.data },
      };

    case 'guides_info':
      return {
        component: RelatedGuides,
        props: { guides: item.attrs?.guides, guidesData: item.data },
      };

    case 'info':
      return {
        component: InfoBlock,
        props: { text: item.attrs?.text },
      };

    case 'banner_placeholder':
      return {
        component: MobileAppPromo,
        props: { kind: item.attrs?.kind },
      };

    case 'vue':
      return getCustomComponentNameAndProps(item);
    default:
      return null;
  }
};

export type ArticleComponentNameAndProps = Exclude<ReturnType<typeof getComponentNameAndProps>, null>;
